import React from "react"
import { useDispatch } from "react-redux"
import { useHistory } from "react-router-dom"
import { resetSearch } from "../actions/searchActions"
import { addressToRedux } from "../actions/locationActions"
import makeSlug from "../utils/makeSlug"
import { logError } from "../utils/errors"
import { GenericStringObj } from "../types"
import { track } from "../utils/analytics"

// The Maps API returns strange locations for some zips. Here's a map.
const LOCATION_HACKS: GenericStringObj = {
  "MONTE VISTA": "Cupertino",
  "MOUNT MADONNA": "Watsonville",
  "CORRAL DE TIE": "Corral-de-Tierra",
  "HUNTINGTN BCH": "Huntington Beach",
  "HILLTOP MALL": "Richmond",
  "CARMEL BY THE": "Carmel-by-the-Sea",
}

const getSearchCity = (city: string) => {
  let searchCity = city.replace(".", "")
  if (city.toUpperCase() in LOCATION_HACKS) {
    searchCity = LOCATION_HACKS[city]
  }
  return searchCity
}

interface ProcessBookingAddressParams {
  setIsSearching?: (isSearching: boolean) => void
  incompleteAddressCallback?: (incomplete: boolean) => void
  doRedirect?: boolean
  lessonType?: string
}

export function useProcessBookingAddress({
  incompleteAddressCallback,
  setIsSearching,
  doRedirect,
  lessonType,
}: ProcessBookingAddressParams) {
  const dispatch = useDispatch()
  const history = useHistory()

  return React.useCallback(
    (address: string) => {
      track("Address Searched")
      dispatch(resetSearch())
      // AddressAutocomplete returns the address the person is/has searched for.
      // So here we need to pull out the city, for the city-location-based
      // meta, and also either the raw address that we'll pass to our scheduling
      // API, or perhaps the already-geocoded result? To be determined.
      dispatch(addressToRedux(address))
        // @ts-ignore
        .then(
          (
            result: {
              city: string
              state: string
              firstResult: google.maps.GeocoderResult
            } | null,
          ) => {
            if (!result) {
              incompleteAddressCallback?.(true)
              setIsSearching?.(false)
              return
            }
            const { city, state } = result
            const searchCity = getSearchCity(city)
            const slug = makeSlug(searchCity)

            incompleteAddressCallback?.(false)

            if (doRedirect) {
              let dest = `/${state.toLowerCase()}/location-search/${slug}`
              if (lessonType) {
                dest = `${dest}/${lessonType}`
              }
              setIsSearching?.(false)
              history.push(dest)
            }
          },
        )
        .catch((error: any) => {
          if (error.message === "INCOMPLETE_ADDRESS_ERROR") {
            incompleteAddressCallback?.(true)
          } else {
            logError(error)
          }
          setIsSearching?.(false)
        })
    },
    [
      dispatch,
      doRedirect,
      history,
      incompleteAddressCallback,
      lessonType,
      setIsSearching,
    ],
  )
}
